// Compliance content for 518 Courier (OBLED Corporation d/b/a 518 Courier)
// PUBLIC content shows only positive, verified operational standards.
// No "pending" / "in progress" registration statuses are shown publicly.
// USDOT / SAM.gov / UEI / CAGE are hidden until an administrator enters and
// activates official values (stored in Firestore: site_compliance/registrations).
// No certification numbers, insurance amounts, UEI, CAGE Code or USDOT number
// is invented anywhere.

export const COMPLIANCE_TITLE = "Compliance, Safety & Service Standards";

export const COMPLIANCE_INTRO =
  "518 Courier operates with documented procedures designed for secure, time-sensitive, medical and business deliveries throughout New York's Capital Region. Our operational documentation is available to qualified clients, healthcare partners and contracting organizations upon request.";

export const COMPLIANCE_FINAL_STATEMENT =
  "Compliance documentation is maintained internally and can be provided to qualified clients, healthcare organizations and contracting partners during the vendor onboarding or contract review process.";

// Public — verified operational standards only (no statuses, positive language).
export const VERIFIED_STANDARDS = [
  { icon: "ClipboardCheck", title: "Chain-of-Custody procedures", text: "Documented custody transfers with signatures and timestamps for sensitive and medical shipments." },
  { icon: "FileCheck", title: "Proof-of-Delivery documentation", text: "Signature, photo and timestamp captured on delivery and available to the client on request." },
  { icon: "UserCheck", title: "Driver background screening", text: "Drivers are screened before being assigned to routes." },
  { icon: "Car", title: "Motor Vehicle Record verification", text: "Driving records are verified as part of driver qualification." },
  { icon: "Wrench", title: "Vehicle inspection procedures", text: "Drivers complete vehicle inspection checks to support safe, reliable transport." },
  { icon: "ShieldCheck", title: "Secure handling of medical & sensitive shipments", text: "Medical and sensitive shipments are handled only by trained, authorized personnel." },
  { icon: "Lock", title: "HIPAA-aligned privacy procedures", text: "HIPAA-aligned privacy and security procedures are used for applicable medical courier operations." },
  { icon: "AlertTriangle", title: "Incident reporting procedures", text: "Defined procedures for reporting incidents, service exceptions and any suspected privacy concern." },
  { icon: "Thermometer", title: "Temperature-control procedures", text: "Temperature-controlled handling is available when the required equipment and procedures are confirmed at booking." },
  { icon: "GraduationCap", title: "Driver training & qualification", text: "Drivers complete training and qualification appropriate to their assigned route types." },
  { icon: "FileText", title: "Commercial insurance documentation", text: "Certificates of insurance are available on request for business and medical accounts." }
];

export const HIPAA_STATEMENT =
  "HIPAA-aligned privacy and security procedures are used for applicable medical courier operations. Business Associate Agreements and supporting compliance documentation are available when required by the client.";

export const MEDICAL_CLARIFICATIONS = [
  "518 Courier provides medical courier services for specimens, pharmaceuticals, medical supplies and documents, subject to client instructions, packaging requirements and applicable federal and New York regulations.",
  "518 Courier does not transport patients or passengers.",
  "Hazardous materials are accepted only when properly classified, packaged, documented and transported under applicable DOT regulations.",
  "Temperature-controlled services are available only when the required equipment and procedures are confirmed at booking.",
  "Medical shipments are handled only by trained and authorized personnel."
];

// Admin-managed government registrations. Kept hidden on the public page until
// an administrator enters an official value AND activates it.
export const REGISTRATION_FIELDS = [
  { key: "usdot", label: "USDOT number" },
  { key: "sam", label: "SAM.gov registration" },
  { key: "uei", label: "Unique Entity ID (UEI)" },
  { key: "cage", label: "CAGE Code" }
];

// Private Compliance Document Center — shared client documents.
export const CLIENT_DOCS = [
  { key: "capability-statement", label: "Capability Statement" },
  { key: "certificate-of-insurance", label: "Certificate of Insurance" },
  { key: "sop-chain-of-custody", label: "Chain-of-Custody SOP" },
  { key: "sop-proof-of-delivery", label: "Proof-of-Delivery SOP" },
  { key: "sop-medical-handling", label: "Medical Shipment Handling SOP" },
  { key: "sop-temperature-control", label: "Temperature-Control SOP" },
  { key: "sop-incident-reporting", label: "Incident Reporting SOP" },
  { key: "baa", label: "Business Associate Agreement (when applicable)" }
];

// Private Compliance Document Center — per-driver documents (uploaded by driver,
// approved by admin, with expiration dates where relevant).
export const DRIVER_DOCS = [
  { key: "hipaa-training", label: "HIPAA privacy training", hasExpiry: true },
  { key: "bloodborne-pathogens", label: "Bloodborne Pathogens training", hasExpiry: true },
  { key: "dot-hazmat", label: "DOT HazMat training", hasExpiry: true },
  { key: "dry-ice", label: "Dry Ice training", hasExpiry: true },
  { key: "medical-courier", label: "Medical Courier training", hasExpiry: true },
  { key: "background-check", label: "Background check", hasExpiry: false },
  { key: "mvr-check", label: "MVR check", hasExpiry: true },
  { key: "vehicle-insurance", label: "Vehicle insurance", hasExpiry: true },
  { key: "sop-acknowledgement", label: "SOP acknowledgement", hasExpiry: false }
];

// Standard Operating Procedures — public summaries. Full documents are available
// to authenticated customers, drivers and administrators only (SOPs page).
export const SOPS = [
  {
    slug: "chain-of-custody",
    title: "Chain-of-Custody SOP",
    summary: "Defines how custody of a shipment is documented from pickup to delivery, including custody transfers, signatures, timestamps and tamper-evidence for sensitive and medical items.",
    points: [
      "Verify sender identity and item description at pickup",
      "Record custody transfer with signature and timestamp",
      "Maintain sealed/tamper-evident handling for sensitive items",
      "Confirm recipient identity and capture proof at delivery"
    ]
  },
  {
    slug: "proof-of-delivery",
    title: "Proof-of-Delivery SOP",
    summary: "Standardizes how delivery is confirmed and evidenced, including recipient signature, photo capture, timestamp and exception handling when a recipient is unavailable.",
    points: [
      "Capture recipient name, signature and timestamp",
      "Photograph delivery location where appropriate",
      "Log delivery exceptions and attempted deliveries",
      "Make POD available to the client on request"
    ]
  },
  {
    slug: "medical-specimen-handling",
    title: "Medical Shipment Handling SOP",
    summary: "Describes safe handling of medical specimens and biological materials by trained, authorized drivers, including packaging verification and privacy safeguards.",
    points: [
      "Verify packaging meets client and regulatory requirements",
      "Handle specimens only with trained, authorized personnel",
      "Apply HIPAA-aligned privacy safeguards",
      "Maintain chain-of-custody throughout transport"
    ]
  },
  {
    slug: "temperature-control",
    title: "Temperature-Control SOP",
    summary: "Applies only when temperature-controlled transport is confirmed at booking. Covers equipment checks and temperature logging for temperature-sensitive shipments.",
    points: [
      "Confirm temperature requirements and equipment at booking",
      "Verify equipment before pickup",
      "Log temperature at required checkpoints where applicable",
      "Escalate temperature exceptions immediately"
    ]
  },
  {
    slug: "dry-ice-handling",
    title: "Dry Ice Handling SOP",
    summary: "Covers safe handling and transport of shipments using dry ice, including ventilation, personal safety and documentation, under applicable DOT requirements.",
    points: [
      "Handle dry ice only with trained personnel",
      "Ensure appropriate ventilation during transport",
      "Follow personal-safety and PPE guidance",
      "Document dry-ice shipments as required"
    ]
  },
  {
    slug: "vehicle-inspection",
    title: "Vehicle Inspection SOP",
    summary: "Standardizes pre-trip and post-trip vehicle inspections to support safe, reliable transport and to identify maintenance issues early.",
    points: [
      "Complete pre-trip inspection checklist",
      "Report defects before dispatch",
      "Complete post-trip inspection and log issues",
      "Remove unsafe vehicles from service"
    ]
  },
  {
    slug: "driver-qualification",
    title: "Driver Qualification SOP",
    summary: "Defines the qualification requirements drivers must meet before assignment, including background checks, MVR checks and required training.",
    points: [
      "Complete background check and MVR verification",
      "Complete required training for assigned route types",
      "Acknowledge relevant SOPs before assignment",
      "Maintain current documents and expiration tracking"
    ]
  },
  {
    slug: "incident-breach-reporting",
    title: "Incident Reporting SOP",
    summary: "Describes how drivers and staff report incidents, service exceptions and any suspected privacy or security concern, and how those reports are handled.",
    points: [
      "Report incidents promptly through the driver portal",
      "Escalate suspected privacy or security concerns immediately",
      "Document facts, time and parties involved",
      "Notify affected clients as required"
    ]
  },
  {
    slug: "hipaa-privacy-security",
    title: "HIPAA Privacy & Security Policy",
    summary: "Summarizes 518 Courier's HIPAA-aligned privacy and security practices for medical courier operations, including access limits, training and Business Associate Agreements.",
    points: [
      "Limit access to protected information to authorized personnel",
      "Apply administrative, physical and technical safeguards",
      "Provide Business Associate Agreements when required",
      "Train personnel on privacy and security responsibilities"
    ]
  }
];
